export interface FileProcessingRoutingRule {
  match?: string;
  isDefault?: boolean;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const globSource = (pattern: string): string =>
  pattern
    .split("")
    .map((character) => {
      if (character === "*") return ".*";
      if (character === "?") return ".";
      return escapeRegExp(character);
    })
    .join("");

const globToRegExp = (pattern: string): RegExp => new RegExp(`^${globSource(pattern)}$`);

/**
 * A glob route matches the file name from its start, or from just after a separator (`_`, `-`, `.` or a space), to its
 * end. So `EP_*` matches "EP_1.csv" and a prefixed "20261007_EP_1.csv", but not the "EP" inside a word, as in
 * "EC_REPLAY.csv" or "EC_SEPTEMBER.csv".
 */
const globRouteRegExp = (pattern: string): RegExp => new RegExp(`(?:^|[_\\-. ])${globSource(pattern)}$`);

/**
 * A pattern that only uses glob wildcards (`*`, `?`) and no regular expression syntax, e.g. `EP_*` or `*.csv`. These
 * are matched as globs (see globRouteRegExp): tried as a regular expression first, `EP_*` would match "EP" anywhere in
 * a name (as in "EC_REPLAY.csv") and route the file to the wrong mapping.
 */
const isGlobPattern = (pattern: string): boolean =>
  /[*?]/.test(pattern) && !/[\\^$+()[\]{}|]/.test(pattern) && !/\.[*?+]/.test(pattern);

export const matchFileProcessingFileName = (fileName: string, pattern?: string): boolean => {
  if (!pattern || typeof pattern !== "string") return false;
  const trimmed = pattern.trim();
  if (!trimmed) return false;

  const regexLiteral = trimmed.match(/^\/(.+)\/([gimsuy]*)$/);
  if (regexLiteral) {
    try {
      return new RegExp(regexLiteral[1], regexLiteral[2]).test(fileName);
    } catch {
      return false;
    }
  }

  if (isGlobPattern(trimmed)) return globRouteRegExp(trimmed).test(fileName);

  try {
    if (new RegExp(trimmed).test(fileName)) return true;
  } catch {
    // Invalid regular expressions may still be valid glob patterns.
  }

  if (trimmed.includes("*") || trimmed.includes("?")) {
    try {
      return globToRegExp(trimmed).test(fileName);
    } catch {
      return false;
    }
  }

  return false;
};

export const selectFileProcessingMappingRule = <TRule extends FileProcessingRoutingRule>(
  rules: readonly TRule[],
  fileName: string,
): TRule | undefined => {
  const matchingRule = rules.find((rule) => rule.match && matchFileProcessingFileName(fileName, rule.match));
  if (matchingRule) return matchingRule;

  const explicitDefault = [...rules].reverse().find((rule) => rule.isDefault === true);
  if (explicitDefault) return explicitDefault;

  const lastRule = rules[rules.length - 1];
  return lastRule && !lastRule.match ? lastRule : undefined;
};

/** Canonicalize saved routing without losing rules from the legacy entities scope. */
export const normalizeFileProcessingScopes = (value: any): any => {
  if (!value || typeof value !== "object" || !("entities" in value)) return value;
  const { entities, ...rest } = value;
  const current = value.extensions;
  const rules = (scope: any): any[] => Array.isArray(scope) ? scope : Array.isArray(scope?.mapping) ? scope.mapping : [];
  const hasMapping = (scope: any) => Array.isArray(scope) || Array.isArray(scope?.mapping);
  const extras = (scope: any) => scope && !Array.isArray(scope) ? scope : {};
  if (!hasMapping(current) && !hasMapping(entities)) {
    return { ...rest, extensions: { ...extras(entities), ...extras(current) } };
  }
  const mapping = [...rules(current), ...rules(entities)].filter((rule, index, all) =>
    all.findIndex(candidate => rule.id && candidate.id ? candidate.id === rule.id : JSON.stringify(candidate) === JSON.stringify(rule)) === index);
  // Select only surviving rules, preferring the canonical scope's effective default.
  const selectedDefault = [...rules(current)].reverse().find(rule => mapping.includes(rule) && rule.isDefault === true)
    ?? [...mapping].reverse().find(rule => rule.isDefault === true);
  const normalized = mapping.map(rule => rule.isDefault === true && rule !== selectedDefault
    ? { ...rule, isDefault: false } : rule);
  return { ...rest, extensions: { ...extras(entities), ...extras(current), mapping: normalized } };
};

/** The worker and test UI still call the extension scope entities internally. */
export const getExtensionFileProcessingScope = (value: any): any => normalizeFileProcessingScopes(value)?.extensions;
